import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api-client";

const SourceStatusSchema = z.object({
  name: z.enum(["log-analytics", "ga4", "auth0", "bigquery"]),
  configured: z.boolean(),
  realAdapter: z.boolean(),
  lastCallAt: z.string().nullable(),
  lastError: z.string().nullable(),
  fallbackInUse: z.boolean(),
});

const DataSourcesSchema = z.object({
  sources: z.array(SourceStatusSchema),
  fallbackEnabled: z.boolean(),
  env: z.string(),
});
export type DataSourcesStatus = z.infer<typeof DataSourcesSchema>;

/**
 * Tells the UI which data sources are real vs running on synthetic stub
 * data, so panels can show an honest "synthetic" badge. Public endpoint,
 * same trust boundary as /health.
 */
export function useDataSources() {
  return useQuery<DataSourcesStatus>({
    queryKey: ["ops", "data-sources"],
    queryFn: async ({ signal }) =>
      DataSourcesSchema.parse(await api.get<unknown>("/api/v1/ops/data-sources", { signal })),
    staleTime: 60_000,
  });
}

/** True when the named source is NOT serving real data (stub or fallback). */
export function isSynthetic(status: DataSourcesStatus | undefined, name: "log-analytics" | "ga4" | "auth0" | "bigquery"): boolean {
  const s = status?.sources.find((x) => x.name === name);
  if (!s) return false; // unknown → don't cry wolf
  return !s.realAdapter || s.fallbackInUse;
}
